import { generatePath, matchPath } from 'react-router';
import { call, put, select, takeEvery, takeLatest, take } from 'redux-saga/effects';

import { navigateToVehicleCheckModelDetails } from 'actions/navigateActions';
import { restClient } from 'config/store/configureStore';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { PATH } from 'constants/RoutesConstants';
import { scopeToPage } from 'modules/app/sagas/utils';
import { navigate } from 'modules/router/navigate';
import { getLocation } from 'modules/router/selectors';
import { createNotification } from 'modules/toast-notifications/actions';
import * as actions from 'modules/vehicleCheckModels/actions';
import * as detailsActions from 'modules/vehicleCheckModels/actions/details';
import * as types from 'modules/vehicleCheckModels/actionTypes';
import { getCurrentLocation } from 'selectors/router';

/** @param {FetchVehicleCheckModelAction} action  */
function* fetchVehicleCheckModel({ payload: { request }, meta }) {
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.fetchVehicleCheckModelSuccess(data, meta));
  } catch (error) {
    yield put(actions.fetchVehicleCheckModelFailure(error, meta));
  }
}

/** @param {FetchVehicleCheckModelsAction} action  */
function* fetchVehicleCheckModels({ payload: { request }, meta }) {
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.fetchVehicleCheckModelsSuccess(data, meta));
  } catch (error) {
    yield put(actions.fetchVehicleCheckModelsFailure(error, meta));
  }
}

/** @param {DeleteVehicleCheckModelAction} action  */
function* deleteVehicleCheckModel({ payload: { request, id }, meta }) {
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteVehicleCheckModelSuccess(id, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.delete.success',
          defaultMessage: 'Vehicle check deleted',
        },
      })
    );
  } catch (error) {
    yield put(actions.deleteVehicleCheckModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.delete.failure',
          defaultMessage: "Can't delete the vehicle check",
        },
      })
    );
  }
}

/** @param {CloneVehicleCheckModelAction} action  */
function* cloneVehicleCheckModel({ payload: { request }, meta }) {
  try {
    const { data: vehicleCheckModel } = yield call(restClient.request, request);
    yield put(actions.cloneVehicleCheckModelSuccess(vehicleCheckModel, meta));

    const origin = yield select(getCurrentLocation);
    yield call(navigateToVehicleCheckModelDetails, vehicleCheckModel.id, { origin });

    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.clone.success',
          defaultMessage: 'Vehicle check model cloned',
        },
      })
    );
  } catch (error) {
    yield put(actions.cloneVehicleCheckModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.clone.failure',
          defaultMessage: "Can't clone the vehicle check model",
        },
      })
    );
  }
}

/** @param {CreateVehicleCheckModelAction} action  */
function* createVehicleCheckModel({ payload: { request }, meta }) {
  try {
    const { data: vehicleCheckModel } = yield call(restClient.request, request);

    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.create.success',
          defaultMessage: 'Vehicle check model created',
        },
      })
    );

    const detailsPath = vehicleCheckModel.dealerId
      ? PATH.VEHICLE_CHECK_MODEL_DETAILED.path
      : PATH.GROUP_VEHICLE_CHECK_MODEL_DETAILED.path;
    const redirectTo = generatePath(detailsPath, {
      vehicleCheckModelId: vehicleCheckModel.id,
    });
    yield call(navigate, redirectTo, { replace: true });
    yield put(actions.createVehicleCheckModelSuccess(vehicleCheckModel, meta));
  } catch (error) {
    yield put(actions.createVehicleCheckModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.create.failure',
          defaultMessage: "Can't create the vehicle check model",
        },
      })
    );
  }
}

/** @param {UpdateVehicleCheckModelAction} action  */
function* updateVehicleCheckModel({ payload: { request }, meta }) {
  try {
    const { data: vehicleCheckModel } = yield call(restClient.request, request);
    yield put(actions.updateVehicleCheckModelSuccess(vehicleCheckModel, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.update.success',
          defaultMessage: 'Vehicle check model updated',
        },
      })
    );
  } catch (error) {
    yield put(actions.updateVehicleCheckModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.vehicleCheckModel.notification.update.failure',
          defaultMessage: "Can't update the vehicle check model",
        },
      })
    );
  }
}

function* initializeDetailsPage() {
  const location = yield select(getLocation);
  const match =
    matchPath(PATH.VEHICLE_CHECK_MODEL_DETAILED.path, location.pathname) ??
    matchPath(PATH.GROUP_VEHICLE_CHECK_MODEL_DETAILED.path, location.pathname);
  const isCreation = match.params.vehicleCheckModelId === NEW_ENTITY_ID;

  if (!isCreation) {
    yield put(actions.fetchVehicleCheckModel(match.params.vehicleCheckModelId, { source: 'details-page' }));
    yield take([types.FETCH_VEHICLE_CHECK_MODEL_SUCCESS, types.FETCH_VEHICLE_CHECK_MODEL_FAILURE]);
  }
  yield put(detailsActions.detailsPageIsReady());
}

function* onDetailPageLoaded() {
  yield call(initializeDetailsPage);
}

export default function* vehicleCheckModelsSagas() {
  yield takeEvery(types.FETCH_VEHICLE_CHECK_MODEL, fetchVehicleCheckModel);
  yield takeLatest(types.CLONE_VEHICLE_CHECK_MODEL, cloneVehicleCheckModel);
  yield takeEvery(types.FETCH_VEHICLE_CHECK_MODELS, fetchVehicleCheckModels);
  yield takeLatest(types.DELETE_VEHICLE_CHECK_MODEL, deleteVehicleCheckModel);
  yield takeLatest(types.CREATE_VEHICLE_CHECK_MODEL, createVehicleCheckModel);
  yield takeLatest(types.UPDATE_VEHICLE_CHECK_MODEL, updateVehicleCheckModel);

  // Details page
  yield scopeToPage([PATH.VEHICLE_CHECK_MODEL_DETAILED, PATH.GROUP_VEHICLE_CHECK_MODEL_DETAILED], onDetailPageLoaded);
}
